import styled from '@emotion/styled';
import { Box, MenuItem, Select, TextField } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../button/CoreButton';
import IconsButton from '../../button/IconsButton';
import Icons from '../../images/Icons';
import {
    ListDescription,
    ListErrorText,
    ListFieldset,
    ListInputBox,
    ListInputRow,
    ListLabel,
} from './CommonListFieldStyles';

interface InputItemListProps {
    initialValues: string[];
    maxItems?: number;
    placeholder?: string;
    legend: string;
    description?: string;
    selectOptions?: { value: string; label: string }[];
    onChange: (values: string[]) => void;
    dialogComponent?: (props: {
        open: boolean;
        onClose: () => void;
        index?: number;
        onCreated?: (newValue: string, index: number) => void;
    }) => React.ReactNode;
    createNewItemLabel?: string;
}

export default function InputItemList({
    initialValues = [''],
    maxItems,
    placeholder = '---',
    legend,
    description,
    selectOptions,
    onChange,
    dialogComponent,
    createNewItemLabel,
}: InputItemListProps) {
    const { t } = useTranslation();
    const [items, setItems] = useState<string[]>(initialValues.length === 0 ? [''] : initialValues);
    const [error, setError] = useState<string | null>();
    const [isDialogOpenProps, setDialogOpenProps] = useState<{ open: boolean; index: number }>({
        open: false,
        index: -1,
    });
    const [errorValues, setErrorValues] = useState<string | null>();
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

    const handleAddItem = () => {
        if (maxItems && items.length + 1 >= maxItems) {
            setError(t('common:input.maximum', 'Max number of items.', { maxItems }));
        }
        setItems((prevItems) => {
            const newItems = [...prevItems, ''];
            // Schedule a focus on the new field
            setTimeout(() => {
                const newIndex = newItems.length - 1;
                inputRefs.current[newIndex]?.focus();
            }, 0);
            return newItems;
        });
    };

    const handleRemoveItem = (index: number) => {
        const updatedItems = items.filter((_, i) => i !== index);
        setItems(updatedItems);
        setError(null);
        onChange?.(updatedItems);

        // Move focus to the next or previous input
        setTimeout(() => {
            if (updatedItems.length === 0) {
                // If no items left, focus the Add button
                buttonRef.current?.focus();
            } else if (inputRefs.current[index]) {
                // Focus the next input if available
                inputRefs.current[index]?.focus();
            } else if (inputRefs.current[index - 1]) {
                // Focus the previous input
                inputRefs.current[index - 1]?.focus();
            }
        }, 0);
    };

    const handleChangeItem = (index: number, value: string) => {
        setError(null);
        setErrorValues(null);
        if (items.includes(value)) {
            setErrorValues(value);
            setError(t('common:input.duplicate'));
        }
        const updatedItems = [...items];
        updatedItems[index] = value;
        setItems(updatedItems);
        onChange?.(updatedItems);
    };

    const handleDialogOpen = (index: number) => {
        setDialogOpenProps({ open: true, index });
    };

    const handleDialogClose = () => {
        setDialogOpenProps({ open: false, index: -1 });
    };

    const handleNewItemCreated = (newValue: string, index?: number) => {
        // get current index of where dialog was opened from
        if (index == null || index === -1) {
            return;
        }
        const currentIndex = index;

        // Update the current empty item with the new value
        const updatedItems = [...items];
        updatedItems[currentIndex] = newValue;

        // Update state and notify parent
        setItems(updatedItems);
        onChange?.(updatedItems);

        // Focus the updated item
        inputRefs.current[currentIndex]?.focus();
    };

    return (
        <ListFieldset>
            <ListLabel as="legend">{legend}</ListLabel>
            {description && <ListDescription>{description}</ListDescription>}
            <ListInputBox role="list">
                {items.map((item, index) => (
                    <ListInputRow role="listitem" key={index}>
                        {selectOptions ? (
                            <Select
                                fullWidth
                                value={item}
                                onChange={(e) =>
                                    e.target.value === '__open_dialog__'
                                        ? handleDialogOpen(index)
                                        : handleChangeItem(index, e.target.value)
                                }
                                aria-label={t('common:input.item', 'Item', { index: index + 1, label: legend })}
                                error={!!error && errorValues === item}
                                inputRef={(el) => (inputRefs.current[index] = el)}>
                                {selectOptions.map((option, i) => (
                                    <MenuItem key={i} value={option.value}>
                                        {option.label || option.value}
                                    </MenuItem>
                                ))}
                                {dialogComponent && (
                                    <CreateMenuItem value="__open_dialog__">
                                        <Icons name="plusLight" />
                                        {createNewItemLabel ?? t('common:input.add')}
                                    </CreateMenuItem>
                                )}
                            </Select>
                        ) : (
                            <TextField
                                fullWidth
                                value={item}
                                placeholder={placeholder}
                                onChange={(e) => handleChangeItem(index, e.target.value)}
                                aria-label={t('common:input.item', 'Item', { index: index + 1, label: legend })}
                                error={!!error && errorValues === item}
                                inputRef={(el) => (inputRefs.current[index] = el)}
                            />
                        )}
                        <IconsButton
                            hideBorder
                            startIcon="cancel2"
                            label={t('common:input.removeItem', 'Remove item', { index: index + 1 })}
                            onClick={() => handleRemoveItem(index)}
                        />
                    </ListInputRow>
                ))}
            </ListInputBox>
            {<ListErrorText role="alert">{error}</ListErrorText>}
            <Box display="flex" alignItems="center" gap={2}>
                {(maxItems == undefined || items.length < maxItems) && (
                    <Button $color="accent" startIcon="plusLight" onClick={handleAddItem} ref={buttonRef}>
                        {t('common:add')}
                    </Button>
                )}
            </Box>
            {dialogComponent &&
                dialogComponent({
                    open: isDialogOpenProps.open,
                    index: isDialogOpenProps.index,
                    onClose: handleDialogClose,
                    onCreated: handleNewItemCreated,
                })}
        </ListFieldset>
    );
}

const CreateMenuItem = styled(MenuItem)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
`;
